import { StopMessage, InitMessage, LoginMessage, RcPauseMessage, GetSysInfoMessage, PongMessage } from '../messages/pocket-com';
import { BaseCommand } from './baseCommand';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import { IAndroidSystemInfo, IInitResponsePacket, IWindowsSystemInfo, ReturnCode } from 'app/modules/remote-control-ng/definitions';
import { MonitorInfo } from '../sysInfo';
import { BehaviorSubject } from 'rxjs';
import { OsDetectionService } from '../../remote-control-ng/services/osDetectionService';
import { inject } from '@angular/core';

export class InitCommand extends BaseCommand<InitMessage> {
    constructor() {
        super(InitMessage);
    }

    protected override doProcess(message: InitMessage): void {
        // Do stuff with init contents
        const session = RcContext.instance.session;
        RcContext.instance.deviceContextHandler.updateWithInitResponse(message.response);
        // console.log("after update deviceContext ", RcContext.instance.deviceContext);
        const returnCode = session.processInitResponse(message.response);
        // // mcrc.log.info("processInitResponse - error code: " + returnCode);

        if (returnCode == ReturnCode.PCR_OK) {
            if (session.isPaused) {
                session.restart({}, true);
                session.pause();
            }

            // // mcrc.appUI.updateDeviceNameLabel();
            // Add a click event handler to trigger the custom event
            session.start()
        }
        else if (returnCode == ReturnCode.PCR_LOGIN_REQUIRED) {
            session.login();
        }
        else {
            session.close();
        }
    }
}

export class InitStreamingCommand extends BaseCommand<InitMessage> {
    constructor() {
        super(InitMessage);
    }

    protected override doProcess(message: InitMessage): void {
        const session = RcContext.instance.session;
        session.processStreamingInitResponse(message.response);
        const msg = new GetSysInfoMessage();
        session.sendCallbackRequest(msg);
    }
}

export class LoginCommand extends BaseCommand<LoginMessage> {
    constructor() {
        super(LoginMessage);
    }

    protected override doProcess(message: LoginMessage): void {
        const loginResponseInfo = message.response;

        RcContext.instance.deviceContextHandler.updateWithLoginResponse(loginResponseInfo);

        const session = RcContext.instance.session;
        session.setLastError(loginResponseInfo.returnCode);
        // HC-5122 add initResponse permission to the new loginResponse
        loginResponseInfo.permissions = session.initResponsePacket ? session.initResponsePacket.permissions : null;

        session.processInitResponse((<unknown>loginResponseInfo) as IInitResponsePacket);

        if (loginResponseInfo.returnCode == ReturnCode.PCR_OK) {
            session.start();
            session.appUI.hideInProgressScreen();
        }
        else {
            session.appUI.hideInProgressScreen();
            session.close();
        }
    }
}

export class LoginStreamingCommand extends BaseCommand<LoginMessage> {
    constructor() {
        super(LoginMessage);
    }

    protected doProcess(message: LoginMessage): void {
        const session = RcContext.instance.session;
        session.processStreamingInitResponse(message.response as IInitResponsePacket);
    }
}

export class PauseCommand extends BaseCommand<RcPauseMessage> {
    constructor() {
        super(RcPauseMessage);
    }

    protected override doProcess(message: RcPauseMessage): void {
        RcContext.instance.session.pause();
    }
}

export class StopCommand extends BaseCommand<StopMessage> {
    constructor() {
        super(StopMessage);
    }

    protected override doProcess(message: StopMessage): void {
        RcContext.instance.session.close();
    }
}

export class SysInfoCommand extends BaseCommand<GetSysInfoMessage> {
    constructor() {
        super(GetSysInfoMessage);
    }

    protected override doProcess(message: GetSysInfoMessage): void {
        if (OsDetectionService.IsAndroid(message.response.main.osType)) {
            // only when init the RC or agent met some errors on monitor, it would send SYSINFO to server
            // update the monitor list
            var monitorList = (<IAndroidSystemInfo>message.response.platformSpecific).monitorList;
            RcContext.instance.multiScreen.monitorList = monitorList;
            for (let i = 0; i < monitorList.length; i++) {
                monitorList[i].rotation$ = new BehaviorSubject(null);
            }

            if (monitorList.length == 1) {
                // console.log("Reset requestMonitorId: " + requestMonitorId + ", and mcrc.utils.messages savedMonitor to " + monitorList[0].id);
                // reset the id of monitor to primary screen
                const previousMonitorId = RcContext.instance.multiScreen.requestMonitorId;
                RcContext.instance.multiScreen.requestMonitorId = monitorList[0].id;
                // means the current monitor is the secondary monitor, but some errors occur on agent side
                // need to switch back to the primary monitors
                if (RcContext.instance.multiScreen.requestMonitorId !== previousMonitorId) {
                    RcContext.instance.multiScreen.updateMonitorInfo(monitorList[0], false);
                }
            }
        } else if (OsDetectionService.IsWindows(message.response.main.osType) && (message.response.platformSpecific as IWindowsSystemInfo)?.monitors) {
            console.log("Processing Get SysInfo")
            // Ideally we should get the monitorList from the agent. This doesn't exist in the MC agent for Windows on 13/7/2023
            const winInfo = message.response.platformSpecific as IWindowsSystemInfo;
            RcContext.instance.multiScreen.supportWinMonitorList = winInfo.monitorsInfo?.length > 1;
            if (RcContext.instance.multiScreen.supportWinMonitorList) {
                for (let i = 0; i < winInfo.monitorsInfo.length; i++) {
                    winInfo.monitorsInfo[i].rotation$ = new BehaviorSubject(null);
                }
                RcContext.instance.multiScreen.monitorList = winInfo.monitorsInfo;
            }
            var monitorList = RcContext.instance.multiScreen.monitorList;
            console.log("Update multiScreen list: " + monitorList);
            if (winInfo.monitors === 1) {
                console.log("Only 1 screen");
                var tmpList = [{
                    id: 0,
                    width: message.response.main.scrWidth,
                    height: message.response.main.scrHeight,
                    rotation: 0,
                    rotation$: new BehaviorSubject(null)
                } as MonitorInfo];
                RcContext.instance.multiScreen.monitorList = tmpList;
                const previousMonitorId = RcContext.instance.multiScreen.requestMonitorId;
                RcContext.instance.multiScreen.requestMonitorId = tmpList[0].id;
                if (RcContext.instance.multiScreen.requestMonitorId !== previousMonitorId) {
                    console.log("Reset to monitor 0")
                    RcContext.instance.multiScreen.updateMonitorInfo(tmpList[0], false);
                }
            } else if (!RcContext.instance.multiScreen.supportWinMonitorList) {
                if (monitorList == null || monitorList.length == 0) {
                    // Windows monitor list for systems with multple displays is 4 entries long:
                    // virtual, primary, secondary, combined. It doesn't arrive from the agent,
                    // so we have to create a hard coded 4 entry list with some display sizes set to 0,
                    // as an indication that the resolution has not been initialized.
                    console.log("Doesn't have monitor list in PC_GETSYSINFO (old Windows Desktop agent)");
                    let id = 0;
                    for (let i = 0; i < 4; i++) {
                        const monitorInfo = new MonitorInfo();
                        monitorInfo.id = id++;
                        RcContext.instance.multiScreen.monitorList.push(monitorInfo);
                    }
                }

                // The display sizes are unknown for all monitors other than the virtual (combined multi displays),
                // arriving in the sysinfo
                const currentMonitor = RcContext.instance.multiScreen.monitorList.find(x => x.id === RcContext.instance.multiScreen.requestMonitorId);
                if (currentMonitor) {
                    currentMonitor.width = message.response.main.scrWidth;
                    currentMonitor.height = message.response.main.scrHeight;
                    RcContext.instance.multiScreen.updateMonitorInfo(currentMonitor);
                }
            }

        }
        RcContext.instance.systemInfo = message.response;
    }
}

// Nothing to do
export class PongCommand extends BaseCommand<PongMessage> {
    constructor() {
        super(PongMessage);
    }
}
